import type { PerformanceMark, RUMEntry } from "@fiverr-private/relay-sdk";
import { performanceEntries } from "../performanceEntries";
import { processEntry } from "../processEntry";
import { sorter } from "../sorter";

/**
 * @type {number}
 */
const MAX_WINDOW_DURATION = 5000;

/**
 * @type {number}
 */
const MAX_GAP_DURATION = 1000;

/**
 * @type {number}
 */
const MIN_PAGE_LIFESPAN = 2000;

/**
 * Return the largest burst is the session window with the maximum cumulative score of all layout shifts within that window. Ignore items with recent user input
 * @see https://web.dev/cls/ and https://web.dev/evolving-cls/
 */
export function cumulativeLayoutShiftMark(): RUMEntry[] {
    if (!PerformanceObserver.supportedEntryTypes.includes("layout-shift")) {
        return [];
    }

    if (performance.now() < MIN_PAGE_LIFESPAN) {
        return [];
    }

    const session = ({ startTime = Number.NEGATIVE_INFINITY } = {}): {
        cls: number;
        entries: PerformanceEntry[];
        startTime: number;
        endTime: number;
    } => ({
        cls: 0,
        entries: [],
        startTime,
        endTime: Number.NEGATIVE_INFINITY,
    });
    const sessions = [session({ startTime: Number.NEGATIVE_INFINITY })];

    performanceEntries({ type: "layout-shift" }).forEach((entry) => {
        const { hadRecentInput, startTime, value } = entry;

        // Ignore user initiated layout shifts
        if (hadRecentInput) {
            return;
        }

        let currentSession = sessions[sessions.length - 1];

        // Create new session window
        if (
            startTime - currentSession.startTime > MAX_WINDOW_DURATION ||
            startTime - currentSession.endTime > MAX_GAP_DURATION
        ) {
            currentSession = session({ startTime });
            sessions.push(currentSession);
        }

        // Accumulate session layout shift values
        currentSession.cls += value;
        currentSession.endTime = startTime;
        currentSession.entries.push(entry);
        entry.counter = currentSession.entries.length;
    });

    const { startTime, endTime, entries, cls } = sessions
        .sort(sorter("cls"))
        .pop();

    const cumulative_layout_shift: PerformanceMark = {
        name: "cumulative_layout_shift",
        entryType: "mark",
        counter: sessions.length,
        duration: endTime - startTime || 0,
        startTime: cls,
        target: undefined,
        metrics: undefined,
        user_action: undefined,
    };

    return [
        processEntry(entries.shift()),
        processEntry(entries.pop()),
        cumulative_layout_shift,
    ].filter(Boolean);
}
